import { useStore } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { ServiceFeatures } from '@mirai/services';
import { Icon, Pressable, Text, Radio, styles, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React from 'react';

import * as style from './Option.module.css';
import { FEATURES } from '../../../../Rates.constants';

const Option = ({
  caption,
  checked = false,
  children,
  disabled,
  hasBasePrice = true,
  highlight,
  icon,
  inline = false,
  name,
  outlined = false,
  title,
  value,
  onPress,
  ...others
}) => {
  const { currencyFormat } = useLocale();
  const { value: { currency, finder: { place: { id: [placeHotelId] = [] } = {} } = {}, hotel: { id } = {} } = {} } =
    useStore();

  const showPriceIncrements = hasBasePrice && ServiceFeatures.get(FEATURES.SHOW_PRICE_INCREMENTS, placeHotelId || id);

  const hasCaption = typeof caption === 'string';
  const hasPrice = onPress && value !== undefined && value !== 0 && (showPriceIncrements ?? true);

  const idAriaLabel = `${others.id || ''}${name || ''}`?.split(' ').join('-');
  const labelledByIds = [];
  if (idAriaLabel) labelledByIds.push(`${idAriaLabel}-title`);
  if (hasCaption) labelledByIds.push(`${idAriaLabel}-caption`);
  if (hasPrice) labelledByIds.push(`${idAriaLabel}-price`);
  const ariaLabelledBy = labelledByIds.join(' ');

  return React.createElement(
    onPress ? Pressable : View,
    {
      disabled,
      ...(onPress ? { preventDefault: false, onPress } : undefined),
      className: styles(
        style.option,
        checked && style.checked,
        disabled && style.disabled,
        onPress && outlined && style.outlined,
        onPress && outlined && inline && style.inline,
        others.className,
      ),
    },
    <>
      {onPress && !outlined && (
        <Radio checked={checked} disabled={disabled} name={name} aria-label={title} aria-labelledby={ariaLabelledBy} />
      )}

      <View className={[style.texts, highlight && style.highlight]}>
        {icon && <Icon headline level={2} value={icon} />}

        <View id={idAriaLabel ? `${idAriaLabel}-title` : undefined} row className={style.content}>
          {typeof title === 'string' ? (
            <Text action medium={outlined || highlight} className={style.title}>
              {title}
            </Text>
          ) : (
            title
          )}
          {children}
        </View>

        {hasCaption ? (
          <Text light small className={style.caption} id={`${idAriaLabel}-caption`}>
            {caption}
          </Text>
        ) : (
          caption
        )}
      </View>
      {hasPrice && (
        <Text action light={value === 0} id={`${idAriaLabel}-price`}>
          {`${value >= 0 ? '+' : '-'} ${currencyFormat({
            currency,
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
            value: Math.abs(value),
          })}`}
        </Text>
      )}
    </>,
  );
};

Option.propTypes = {
  caption: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  checked: PropTypes.bool,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  hasBasePrice: PropTypes.bool,
  highlight: PropTypes.bool,
  icon: PropTypes.any,
  inline: PropTypes.bool,
  name: PropTypes.string,
  outlined: PropTypes.bool,
  title: PropTypes.string,
  value: PropTypes.number,
  onPress: PropTypes.func,
};

export { Option };
